import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    OnInit,
    output,
    ViewEncapsulation,
    signal,
    DoCheck,
    HostBinding,
} from '@angular/core';
import { FlooriInputComponent } from '../floori-input';
import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR,
    ControlContainer,
    FormGroupDirective,
    FormControl,
} from '@angular/forms';
import { Languages } from '../../models/language';
import { FlooriControl } from '../../utils';
import { FieldTranslation } from '../../models/field-translation';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '../../modules/translations/pipes';
import { TranslationsModule } from '../../modules/translations';
import { FlooriButtonComponent } from '../floori-button';
import { takeUntil } from 'rxjs';

@Component({
    selector: 'floori-translation-input',
    standalone: true,
    imports: [FlooriInputComponent, FlooriButtonComponent, CommonModule, TranslationsModule],
    providers: [
        TranslatePipe,
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FlooriTranslationInputComponent,
        },
    ],
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
    templateUrl: './floori-translation-input.component.html',
    styleUrl: './floori-translation-input.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
})
export class FlooriTranslationInputComponent
    extends FlooriControl
    implements ControlValueAccessor, OnInit, DoCheck
{
    private readonly translationPipe = inject(TranslatePipe);
    private readonly controlContainer = inject(ControlContainer, { optional: true });

    readonly languages = input<Languages[]>(Object.values(Languages));
    readonly placeholder = input(this.translationPipe.transform('productForm.namePlaceholder'));
    readonly multiline = input<boolean>(false);
    readonly labelPosition = input<'left' | 'right'>('right');
    readonly firstControlName = input<string | null>(null);

    readonly showSecondField = input<boolean>(false);
    readonly secondPlaceholder = input<string>('');
    readonly secondControlName = input<string | null>(null);

    readonly isCollapsible = input<boolean>(true);
    readonly defaultExpanded = input<boolean>(false);
    readonly isExpanded = signal<boolean>(false);

    readonly valueChanges = output<FieldTranslation>();

    readonly sortedLanguages = computed(() =>
        this.languages().sort((a, b) => {
            const translationA = this.translationPipe.transform(`lang.${a}`);
            const translationB = this.translationPipe.transform(`lang.${b}`);
            return translationA.localeCompare(translationB);
        }),
    );

    @HostBinding('class')
    override get classBindings(): string[] {
        const controlToCheck = this.firstCtrl || this.control;
        const isInvalid =
            controlToCheck &&
            (!controlToCheck.pristine || controlToCheck.touched) &&
            controlToCheck.invalid;

        if (this._invalid !== isInvalid) {
            this._invalid = isInvalid || false;
        }

        const baseClasses = super.classBindings;

        return ['floori-translation-input', ...baseClasses].filter(c => !!c);
    }

    private _value: FieldTranslation = { default: '' };
    private firstCtrl: FormControl<FieldTranslation> | null = null;
    private secondCtrl: FormControl<FieldTranslation> | null = null;

    get value(): FieldTranslation {
        return this._value;
    }

    set value(val: FieldTranslation) {
        this._value = val || { default: '' };
        this.onChange(this._value);
        this.cdr.detectChanges();
    }

    writeValue(value: FieldTranslation): void {
        this.value = value || { default: '' };
    }

    override registerOnChange(fn: (value: FieldTranslation) => void): void {
        this.onChange = fn;
    }

    override ngOnInit(): void {
        super.ngOnInit();

        this.isExpanded.set(this.defaultExpanded());

        const container = this.controlContainer?.control as any;
        if (container) {
            const firstName = this.firstControlName();
            const secondName = this.secondControlName?.() as string | null | undefined;
            if (firstName) {
                this.firstCtrl = container.get(firstName);
                if (this.firstCtrl) {
                    const initial = this.firstCtrl.value || { default: '' };
                    this.writeValue(initial);
                    this.firstCtrl.valueChanges
                        ?.pipe(takeUntil(this.destroyed))
                        .subscribe((val: FieldTranslation) => {
                            if (val) {
                                this._value = val;
                            }

                            this.cdr.markForCheck();
                        });
                    this.firstCtrl.statusChanges?.pipe(takeUntil(this.destroyed)).subscribe(() => {
                        this.cdr.markForCheck();
                    });
                }
            }
            if (secondName) {
                this.secondCtrl = container.get(secondName);
            }
        }
    }

    ngDoCheck(): void {
        const controlToCheck = this.firstCtrl || this.control;
        if (controlToCheck) {
            const isInvalid =
                (!controlToCheck.pristine || controlToCheck.touched) && controlToCheck.invalid;
            if (this._invalid !== isInvalid) {
                this._invalid = isInvalid || false;
                this.cdr.markForCheck();
            }
        }
    }

    updateTranslation(languageCode: string, translationValue: string): void {
        const updatedValue = { ...this._value };

        updatedValue[languageCode as keyof FieldTranslation] = translationValue;

        this.value = updatedValue;
        if (this.firstCtrl) {
            this.firstCtrl.setValue(updatedValue);
            this.firstCtrl.markAsDirty();
            this.firstCtrl.markAsTouched();
        } else {
            this.valueChanges.emit(updatedValue);
        }
    }

    updateSecondTranslation(languageCode: string, translationValue: string): void {
        const current = (this.secondCtrl?.value as FieldTranslation) || { default: '' };
        const updatedValue: FieldTranslation = { ...current } as FieldTranslation;
        updatedValue[languageCode as keyof FieldTranslation] = translationValue;
        if (this.secondCtrl) {
            this.secondCtrl.setValue(updatedValue);
            this.secondCtrl.markAsDirty();
            this.secondCtrl.markAsTouched();
        }
    }

    getTranslationForLanguage(languageCode: string): string {
        return this._value[languageCode as keyof FieldTranslation] || '';
    }

    getSecondTranslationForLanguage(languageCode: string): string {
        const second = (this.secondCtrl?.value as FieldTranslation) || { default: '' };
        return second[languageCode as keyof FieldTranslation] || '';
    }

    onFocusChange(focused: boolean): void {
        this.focusChanged(focused);
    }

    toggleExpanded(): void {
        if (!this.isCollapsible()) {
            return;
        }
        this.isExpanded.update(v => !v);
    }
}
